import { Component, ElementRef, inject, ViewChild } from '@angular/core';
import { SidebarService } from '../../services/sidebar.service';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { ThemeToggleButtonComponent } from '../../components/common/theme-toggle/theme-toggle-button.component';
import { NotificationDropdownComponent } from '../../components/header/notification-dropdown/notification-dropdown.component';
import { UserDropdownComponent } from '../../components/header/user-dropdown/user-dropdown.component';
import { GlobalSearchDropdownComponent } from '../../components/header/global-search/global-search-dropdown/global-search-dropdown.component';
import { EMPTY_SEARCH_RESULTS, SearchResult, SearchResults } from '../../components/header/global-search/models/global-search.model';
import { debounceTime, distinctUntilChanged, Subject, switchMap, takeUntil } from 'rxjs';
import { GlobalSearchService } from '../../components/header/global-search/services/global-search.service';
import { QuickActionsMenuComponent } from '../../components/quick-actions-menu/quick-actions-menu.component';
import { AgentPresenceSelectorComponent } from '../../../pages/call/agent-presence-selector/agent-presence-selector.component';
import { canUseCallDialer } from '../../../pages/call/call.constants';
import { AuthService } from '../../../pages/auth-pages/services/auth.service';
import { EmailComposeService } from '../../../pages/correos/services/email-compose.service';
import { NotificationService } from '../../../pages/notifications/services/notification.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ThemeToggleButtonComponent,
    NotificationDropdownComponent,
    UserDropdownComponent,
    GlobalSearchDropdownComponent,
    QuickActionsMenuComponent,
    AgentPresenceSelectorComponent,
  ],
  templateUrl: './app-header.component.html',
})
export class AppHeaderComponent {
  isApplicationMenuOpen = false;
  readonly isMobileOpen$;

  // Estado del buscador global
  searchTerm = '';
  searchResults: SearchResults = { ...EMPTY_SEARCH_RESULTS };
  isDropdownVisible = false;

  // Visibilidad del selector de presencia (Sales Rep hacia arriba) — ver call.constants.ts
  readonly showPresenceSelector: boolean;

  private searchInput$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  private globalSearch = inject(GlobalSearchService);
  private router = inject(Router);
  private auth = inject(AuthService);
  private emailCompose = inject(EmailComposeService);
  private notificationService = inject(NotificationService);

  constructor(public sidebarService: SidebarService) {
    this.isMobileOpen$ = this.sidebarService.isMobileOpen$;
    this.showPresenceSelector = canUseCallDialer(this.auth.getCurrentUser()?.roleName);

    // El header solo se renderiza dentro del layout autenticado: arrancar aquí
    // el polling del badge de notificaciones (idempotente).
    this.notificationService.startPolling();

    // Pipeline de búsqueda con debounce
    this.searchInput$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(term => {
        if (!term || term.trim().length < 2) {
          this.searchResults = { ...EMPTY_SEARCH_RESULTS };
          this.isDropdownVisible = false;
          return [];
        }
        this.searchResults = { ...EMPTY_SEARCH_RESULTS, isLoading: true };
        this.isDropdownVisible = true;
        return this.globalSearch.search(term);
      }),
      takeUntil(this.destroy$)
    ).subscribe(results => {
      if (results) this.searchResults = results;
    });
  }

  /** Abre el compose flotante global vacío (sin prefill). */
  abrirCompose() {
    this.emailCompose.open();
  }

  handleToggle() {
    if (window.innerWidth >= 1280) {
      this.sidebarService.toggleExpanded();
    } else {
      this.sidebarService.toggleMobileOpen();
    }
  }

  toggleApplicationMenu() {
    this.isApplicationMenuOpen = !this.isApplicationMenuOpen;
  }

  ngAfterViewInit() {
    document.addEventListener('keydown', this.handleKeyDown);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.notificationService.stopPolling();
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  handleKeyDown = (event: KeyboardEvent) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      this.searchInput?.nativeElement.focus();
    }
    if (event.key === 'Escape') {
      this.isDropdownVisible = false;
      this.searchInput?.nativeElement.blur();
    }
  };

  onSearchInput(value: string) {
    this.searchTerm = value;
    this.searchInput$.next(value);
  }

  onSearchFocus() {
    if (this.searchTerm.length >= 2) {
      this.isDropdownVisible = true;
    }
  }

  onSearchBlur() {
    // Delay para permitir clic en un resultado antes de cerrar
    setTimeout(() => { this.isDropdownVisible = false; }, 200);
  }

  onResultClicked(result: SearchResult) {
    this.router.navigateByUrl(result.routerLink);
    this.isDropdownVisible = false;
    this.searchTerm = '';
    this.searchResults = { ...EMPTY_SEARCH_RESULTS };
    // Reiniciar también el stream: `distinctUntilChanged` conserva el último
    // término, así que sin este reset volver a teclear EXACTAMENTE lo mismo
    // (buscar → abrir el detalle → cerrar → buscar igual) no volvía a emitir y
    // el desplegable no reaparecía.
    this.searchInput$.next('');
  }
}
